import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { StandardIngredient, ExtractedIngredient, MatchedIngredient } from '../types/ingredients';
import { normalizeString } from '../utils/stringUtils';

export class IngredientMatchingService {
  private standardIngredients: StandardIngredient[] = [];
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    await this.loadIngredients();
  }

  /**
   * Force reload ingredients from Firestore
   * Useful after adding aliases or creating new ingredients
   */
  async reload(): Promise<void> {
    console.log('🔄 Reloading ingredients from Firestore...');
    this.initialized = false;
    await this.loadIngredients();
  }

  private async loadIngredients(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, 'standardIngredients'));
      this.standardIngredients = [];
      snapshot.forEach((doc) => {
        this.standardIngredients.push({ id: doc.id, ...doc.data() } as StandardIngredient);
      });
      
      // Filter by browser language
      const browserLanguage = navigator.language.split('-')[0].toLowerCase() as 'en' | 'fr';
      this.standardIngredients = this.standardIngredients.filter(
        ing => ing.language?.toLowerCase() === browserLanguage
      );
      
      this.initialized = true;
      console.log(`✅ Loaded ${this.standardIngredients.length} standard ingredients`);
    } catch (error) {
      console.error('❌ Error loading standard ingredients:', error);
      throw error;
    }
  }

  findMatch(extractedIngredient: ExtractedIngredient): MatchedIngredient {
    if (!this.initialized) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    const normalizedName = normalizeString(extractedIngredient.name);
    let bestMatch: StandardIngredient | undefined;
    let confidence: 'high' | 'medium' | 'low' | 'none' = 'none';

    // 1. Exact match on name
    bestMatch = this.standardIngredients.find(
      ing => normalizeString(ing.name) === normalizedName
    );
    if (bestMatch) {
      confidence = 'high';
      return { extracted: extractedIngredient, matched: bestMatch, confidence };
    }

    // 2. Exact match on aliases
    bestMatch = this.standardIngredients.find(ing =>
      ing.aliases?.some(alias => normalizeString(alias) === normalizedName)
    );
    if (bestMatch) {
      confidence = 'high';
      return { extracted: extractedIngredient, matched: bestMatch, confidence };
    }

    // 3. Starts with match
    bestMatch = this.standardIngredients.find(
      ing => normalizeString(ing.name).startsWith(normalizedName)
    );
    if (bestMatch) {
      confidence = 'medium';
      return { extracted: extractedIngredient, matched: bestMatch, confidence };
    }

    // 4. Contains match
    bestMatch = this.standardIngredients.find(
      ing => normalizeString(ing.name).includes(normalizedName)
    );
    if (bestMatch) {
      confidence = 'medium';
      return { extracted: extractedIngredient, matched: bestMatch, confidence };
    }

    // 5. Alias contains match
    bestMatch = this.standardIngredients.find(ing =>
      ing.aliases?.some(alias => normalizeString(alias).includes(normalizedName))
    );
    if (bestMatch) {
      confidence = 'low';
      return { extracted: extractedIngredient, matched: bestMatch, confidence };
    }

    // No match found
    return { extracted: extractedIngredient, matched: undefined, confidence: 'none' };
  }

  matchIngredients(extractedIngredients: ExtractedIngredient[]): MatchedIngredient[] {
    return extractedIngredients.map(ing => this.findMatch(ing));
  }

  searchIngredients(query: string, limit: number = 10): StandardIngredient[] {
    if (!this.initialized) return [];
    if (!query.trim()) return this.standardIngredients.slice(0, limit);

    const normalizedQuery = normalizeString(query);

    return this.standardIngredients
      .filter(ingredient => {
        const normalizedName = normalizeString(ingredient.name);
        const nameMatch = normalizedName.includes(normalizedQuery);
        const aliasMatch = ingredient.aliases?.some(alias =>
          normalizeString(alias).includes(normalizedQuery)
        );
        return nameMatch || aliasMatch;
      })
      .sort((a, b) => {
        // Prioritize exact matches
        const aExact = normalizeString(a.name) === normalizedQuery;
        const bExact = normalizeString(b.name) === normalizedQuery;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        // Then starts with
        const aStartsWith = normalizeString(a.name).startsWith(normalizedQuery);
        const bStartsWith = normalizeString(b.name).startsWith(normalizedQuery);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // Alphabetically
        return a.name.localeCompare(b.name);
      })
      .slice(0, limit);
  }
}
